// Pull requests (HRTH-115): each is a log of events encrypted under the repository's
// library key; the server keeps the log and whether it's open, closed or merged. Diffs,
// reviews and merges are worked out here. Merging writes the merge (if any) as a new pack,
// uploads it like a push, then moves the target branch with compare-and-swap.

import type { Pull, PullActivity, PullDetail, PullEvent, PullState, RepoRef, UserId } from '../api/types'
import { fileEncryptor } from './attachments'
import { type Repository } from './git'
import type { BlobApi } from './libraryUpload'
import { uploadBlob } from './libraryUpload'
import { blindRef, decryptName, encryptName } from './metacrypto'

export type Verdict = 'approve' | 'changes' | 'comment'

/**
 * Whether this device should record that a pull request's source branch moved from
 * `recorded` to `live` (its own view of the branch). Only when it knows the recorded commit
 * and isn't behind it: a device with stale refs would otherwise record its older commit,
 * and two open pages would correct each other forever (HRTH-140). Force pushes, where
 * neither commit descends from the other, are recorded.
 */
export function shouldRecordPush(
  repo: Pick<Repository, 'has' | 'mergeBase'>,
  recorded: string,
  live: string | undefined,
): boolean {
  if (!live || live === recorded || !repo.has(recorded) || !repo.has(live)) return false
  return repo.mergeBase(live, recorded) !== live
}

/** Where a line comment sits: a file, and a line on one side of its diff. */
export interface LineAnchor {
  path: string
  side: 'old' | 'new'
  line: number
}

export type PullBody =
  | { type: 'opened'; title: string; description: string; source: string; target: string; head: string }
  | { type: 'updated'; head: string }
  | { type: 'comment'; text: string; at?: LineAnchor; head?: string }
  | { type: 'review'; verdict: Verdict; text: string; head: string }
  | { type: 'closed' }
  | { type: 'reopened' }
  | { type: 'merged'; commit: string; how: 'fast_forward' | 'merge_commit' }

const kind = (library: string) => `repo-pull:${library}` as const

export function encryptPullBody(key: string, library: string, body: PullBody): Promise<string> {
  return encryptName(key, kind(library), JSON.stringify(body))
}

export async function decryptPullBody(key: string, library: string, e: PullEvent): Promise<PullBody | null> {
  const json = await decryptName(key, kind(library), e.body)
  if (!json || json === e.body) return null
  try {
    return JSON.parse(json) as PullBody
  } catch {
    return null
  }
}

export interface TimelineItem {
  id: string
  author?: UserId
  at: number
  body: PullBody
}

/** A pull request as its log adds up. */
export interface PullView {
  id: string
  number: number
  state: PullState
  author?: UserId
  title: string
  description: string
  source: string
  target: string
  /** The source branch's head when the log last recorded it. */
  head: string
  /** Each reviewer's latest verdict (approve / changes), and on which head. */
  reviews: Map<UserId, { verdict: Verdict; head: string; at: number }>
  timeline: TimelineItem[]
}

export async function viewPull(key: string, library: string, detail: PullDetail): Promise<PullView | null> {
  const items: TimelineItem[] = []
  for (const e of detail.events) {
    const body = await decryptPullBody(key, library, e)
    if (body) items.push({ id: e.id, ...(e.author ? { author: e.author } : {}), at: e.created_at, body })
  }
  const opened = items.find((i) => i.body.type === 'opened')?.body
  if (!opened || opened.type !== 'opened') return null
  const v: PullView = {
    id: detail.pull.id,
    number: detail.pull.number,
    state: detail.pull.state,
    ...(detail.pull.author ? { author: detail.pull.author } : {}),
    title: opened.title,
    description: opened.description,
    source: opened.source,
    target: opened.target,
    head: opened.head,
    reviews: new Map(),
    timeline: items,
  }
  for (const i of items) {
    if (i.body.type === 'updated') v.head = i.body.head
    if (i.body.type === 'review' && i.author && i.body.verdict !== 'comment') {
      v.reviews.set(i.author, { verdict: i.body.verdict, head: i.body.head, at: i.at })
    }
  }
  return v
}

/** A list entry: title and branches from the opening event. */
export async function pullSummary(key: string, library: string, p: Pull): Promise<{ title: string; source: string; target: string } | null> {
  const b = await decryptPullBody(key, library, p.opened)
  return b?.type === 'opened' ? { title: b.title, source: b.source, target: b.target } : null
}

// --- Unread activity -------------------------------------------------------------------

/**
 * Pull requests with events this device hasn't seen (`seen`: events counted when last
 * looked at), not counting ones whose latest event is the reader's own.
 */
export function unreadPulls(activity: PullActivity[], seen: Record<string, number>, me: UserId | null): PullActivity[] {
  return activity.filter((p) => p.events > (seen[p.id] ?? 0) && p.last_author !== me)
}

/** A line for a notification about an event. */
export function describeEvent(b: PullBody, title: string): string {
  switch (b.type) {
    case 'opened':
      return `opened “${b.title}”`
    case 'updated':
      return `pushed to “${title}”`
    case 'comment':
      return b.text
    case 'review':
      return `${b.verdict === 'approve' ? 'approved' : b.verdict === 'changes' ? 'asked for changes' : 'reviewed'}${b.text ? `: ${b.text}` : ''}`
    case 'closed':
      return `closed “${title}”`
    case 'reopened':
      return `reopened “${title}”`
    case 'merged':
      return `merged “${title}”`
  }
}

// --- Diff lines ---------------------------------------------------------------------------

export interface DiffLine {
  text: string
  kind: 'hunk' | 'add' | 'del' | 'same'
  /** Line numbers in the old and new file, where the line exists. */
  old?: number
  new?: number
}

/** A unified diff as lines with their numbers on each side (for line comments). */
export function diffLines(patch: string): DiffLine[] {
  const out: DiffLine[] = []
  let o = 0
  let n = 0
  for (const text of patch.split('\n')) {
    if (text.startsWith('---') || text.startsWith('+++')) continue
    const h = /^@@ -(\d+)(?:,\d+)? \+(\d+)(?:,\d+)? @@/.exec(text)
    if (h) {
      o = Number(h[1])
      n = Number(h[2])
      out.push({ text, kind: 'hunk' })
    } else if (text.startsWith('+')) out.push({ text, kind: 'add', new: n++ })
    else if (text.startsWith('-')) out.push({ text, kind: 'del', old: o++ })
    else if (text.startsWith(' ')) out.push({ text, kind: 'same', old: o++, new: n++ })
  }
  return out
}

/** The anchor a comment on this line gets: the new side, unless the line was removed. */
export function anchorOf(path: string, l: DiffLine): LineAnchor | null {
  if (l.kind === 'del' && l.old) return { path, side: 'old', line: l.old }
  if (l.new) return { path, side: 'new', line: l.new }
  return null
}

export const sameAnchor = (a: LineAnchor | undefined, b: LineAnchor) => !!a && a.path === b.path && a.side === b.side && a.line === b.line

// --- Merging ---------------------------------------------------------------------------

export type MergeOutcome =
  | { kind: 'up_to_date' }
  | { kind: 'fast_forward'; commit: string }
  | { kind: 'commit'; commit: string }
  | { kind: 'conflicts'; paths: string[] }

export interface MergeApi extends BlobApi {
  repoRefs(library: string): Promise<{ refs: RepoRef[] }>
  updateRefs(library: string, updates: { id: string; expected: number; target?: string }[]): Promise<unknown>
  addLibraryEntry(library: string, meta: string): Promise<{ id: string }>
  deleteLibraryEntry(entry: string): Promise<void>
}

/** What merging `source` into `target` would do, without doing it. */
export function previewMerge(repo: Repository, targetSha: string, sourceSha: string): MergeOutcome {
  return repo.merge(targetSha, sourceSha, 'preview', 'hrth', 'hrth', 0)
}

/**
 * Merges a pull request's source into its target and moves the target branch. A merge
 * commit's objects are uploaded first, as an encrypted pack. Throws on conflicts or if the
 * target moved meanwhile (`ref_conflict`).
 */
export async function mergePull(
  api: MergeApi,
  repo: Repository,
  library: string,
  key: string,
  v: { target: string; source: string; number: number; title: string },
  sourceSha: string,
  who: { name: string; email: string },
): Promise<{ commit: string; how: 'fast_forward' | 'merge_commit' }> {
  const refs = (await api.repoRefs(library)).refs
  const targetId = await blindRef(key, library, v.target)
  const current = refs.find((r) => r.id === targetId)
  if (!current?.target) throw new Error(`${v.target} doesn't exist any more`)
  const json = await decryptName(key, `repo-ref:${library}`, current.target)
  const targetSha = json ? (JSON.parse(json) as { sha: string }).sha : ''
  const message = `Merge #${v.number}: ${v.title}\n\nMerges ${v.source.replace('refs/heads/', '')} into ${v.target.replace('refs/heads/', '')}.`
  const m = repo.merge(targetSha, sourceSha, message, who.name, who.email, Math.floor(Date.now() / 1000)) as MergeOutcome
  if (m.kind === 'conflicts') throw new Error(`both branches changed ${m.paths.join(', ')}; merge locally with git`)
  if (m.kind === 'up_to_date') return { commit: targetSha, how: 'fast_forward' }
  if (m.kind === 'commit') {
    const pack = repo.takePack()
    repo.addPack(pack)
    const enc = await fileEncryptor(new Blob([new Uint8Array(pack)]))
    const meta = await encryptName(key, `library-entry:${library}`, JSON.stringify({ kind: 'git-pack', info: enc.info }))
    const entry = await api.addLibraryEntry(library, meta)
    try {
      await uploadBlob(api, entry.id, 'original', enc)
    } catch (e) {
      await api.deleteLibraryEntry(entry.id).catch(() => undefined)
      throw e
    }
  }
  const target = await encryptName(key, `repo-ref:${library}`, JSON.stringify({ name: v.target, sha: m.commit }))
  await api.updateRefs(library, [{ id: targetId, expected: current.version, target }])
  return { commit: m.commit, how: m.kind === 'commit' ? 'merge_commit' : 'fast_forward' }
}
